import {
	type AnyPipelineMessageStageProcessor,
	type AnyPipelineMessageStageProcessorAsyncExecute,
	type PipelineOptions,
} from '@post-office/shared-contracts/pipeline/types';
import type { PipelineMessage } from '@post-office/shared-contracts/types';

import { type PlacementStageProcessorDependencies } from '../../../placement-orchestration/types';

import { type RuntimeParams } from '../../create-placement-component/types';
import { PlacementSystemError } from '../../error';
import {
	type AnyBackendMessageBody,
	type AnyBackendPlacementStage,
	type AnyPayload,
	type PlacementBackendRuntime,
	type PlacementStages,
	type RequestContext,
} from '../../types';
import { createPlacementStages } from '../../util/runtime';
import { mergePlacementConfigs } from '../../util/runtime/tunnel/merge-placement-configs';
import { TunnelPlacementBackendRuntimeConfigBuilder } from '../../test-util/tunnel/placement-backend-runtime-config';
import { checkShouldTunnelStage } from './tunnelling/tunnel-decision';
import { executePlacementStageTunneled } from './tunnelling/execute-tunnel-placement-stage';

const TUNNEL_FEATURE_GATE = 'post-office_enable-in-app-v2-placement-tunnel';

type TunnelDependenciesFromKoaContext = {
	whitelistProxyHost: string;
	allowRemoteExecution: boolean;
};
type TunnelDependenciesFromPlacementPipeline = PlacementStageProcessorDependencies;
type TunnelDependencies = TunnelDependenciesFromKoaContext &
	TunnelDependenciesFromPlacementPipeline;

type PlacementPipelineOptions = PipelineOptions<{
	depExtensions: PlacementStageProcessorDependencies;
	params: Record<string, unknown>;
}>;

export const productionRuntime: PlacementBackendRuntime = (baseConfig) => {
	const tunnelPlacementBackendRuntimeConfigBuilder =
		new TunnelPlacementBackendRuntimeConfigBuilder();

	return ({ koa, request }) => {
		const enableBypassBuffer = request.statsig.checkGate(
			{
				identifiers: {
					atlassianAccountId: request?.orchestrationContext?.recipient?.toString(),
				},
			},
			'post-office_enable_bypass-data',
		);

		// This is stateful mechanism to allow placement stages to send none message data between them.
		// Examples include grouping data, or other metadata provided by the placement.
		// Orchestration messageSelection only accepts PipelineMessage[] as an input, which means we can't add it to the pipeline payload. >(ভ⤙ ভ ")<
		// Be aware that because this is stateful it is dangerous. Be sure that this only is instantiated per request to prevent metadata
		// cross contamination between requests
		const bypassBuffer = createBypassBuffer();

		const orchestrationRequest = request.location.tag({ placementLevel: 'PostOffice' });

		return async (params: RuntimeParams) => {
			let finalConfig = baseConfig;
			const isTunnelEnabled = request.statsig.checkGate(
				{
					identifiers: {
						atlassianAccountId: request.orchestrationContext.recipient.userId,
					},
				},
				TUNNEL_FEATURE_GATE,
			);

			if (isTunnelEnabled) {
				try {
					const placementId = baseConfig.placementId;

					const tunnelledConfig =
						await tunnelPlacementBackendRuntimeConfigBuilder.buildConfigOverlay({
							placementId,
							params,
							requestContext: request,
							baseConfig,
						});

					finalConfig = mergePlacementConfigs(baseConfig, tunnelledConfig);

					request.logger.info(
						{
							placementId,
							hasTunnelledConfig: Object.keys(tunnelledConfig).length > 0,
						},
						'[Tunnel] Config overlay applied',
					);
				} catch (error) {
					request.logger.error(
						{
							error,
							placementId: baseConfig.placementId,
						},
						'[Tunnel] Failed to build overlay config, falling back to base config',
					);
					finalConfig = baseConfig;
				}
			}

			const tunnelDepsFromKoaContext = {
				whitelistProxyHost: koa.ctx.config.micros.whitelistProxyHost,
				allowRemoteExecution: koa.ctx.config.remoteExecution.allowRemoteExecution,
			};

			const placementStages = createPlacementStages(finalConfig);
			const stageProcessors = placementStagesToStageProcessors(
				placementStages,
				tunnelDepsFromKoaContext,
			);
			const stages = stageProcessors({ request, bypassBuffer, enableBypassBuffer });

			try {
				const messages = await koa.ctx.messageSelection.getEligibleMessages(orchestrationRequest, {
					...params,
					stages,
				});

				if (enableBypassBuffer) {
					const bypassData = bypassBuffer.getOnce();

					return {
						data: { ...bypassData, messages },
						// Add error output when compatible with orchestration
						errors: [],
					};
				}

				return {
					data: { messages },
					// Add error output when compatible with orchestration
					errors: [],
				};
			} finally {
				request.messagePerformance?.stop();
			}
		};
	};
};

const placementStagesToStageProcessors =
	(placementStages: PlacementStages, tunnelDepsFromKoaContext: TunnelDependenciesFromKoaContext) =>
	(contexts: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
	}): { [stageName in keyof PlacementStages]: AnyPipelineMessageStageProcessor } => {
		const stageProcessors: {
			[stageName in keyof PlacementStages]: AnyPipelineMessageStageProcessor;
		} = {};

		Object.entries(placementStages).forEach((stage) => {
			const [stageName] = stage;

			stageProcessors[stageName as keyof PlacementStages] = placementStageToStageProcessor({
				...contexts,
				tunnelDepsFromKoaContext,
			})(stage);
		});

		return stageProcessors;
	};

const placementStageToStageProcessor =
	({
		request,
		bypassBuffer,
		enableBypassBuffer,
		tunnelDepsFromKoaContext,
	}: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
		tunnelDepsFromKoaContext: TunnelDependenciesFromKoaContext;
	}) =>
	([stageName, placementStage]: [
		stageName: string,
		placementStage: AnyBackendPlacementStage | undefined,
	]): AnyPipelineMessageStageProcessor => {
		return (options: PlacementPipelineOptions) => {
			const { tunnelTestingOverridesService, userDetailsProvider, tunnelManagerFactory } =
				options.deps;

			const tunnelDependencies = {
				...tunnelDepsFromKoaContext,
				tunnelTestingOverridesService,
				userDetailsProvider,
				tunnelManagerFactory,
			};

			// Create execute function with access to tunnelDependencies
			const execute = placementStageToStageProcessorExecute(placementStage)({
				request,
				bypassBuffer,
				enableBypassBuffer,
				tunnelDependencies,
				stageName,
			});

			return {
				id: stageName,
				execute,
			};
		};
	};

type BypassBuffer<T extends AnyPayload = AnyPayload> = {
	set: (buffer: T) => void;
	getOnce: () => T | undefined;
	flush: () => void;
};

const createBypassBuffer = <T extends AnyPayload = AnyPayload>(): BypassBuffer<T> => {
	let state: T | undefined = undefined;

	const getOnce: BypassBuffer<T>['getOnce'] = () => {
		const once = state;
		flush();

		return once;
	};

	const set: BypassBuffer<T>['set'] = (buffer) => {
		state = buffer;
	};

	const flush: BypassBuffer<T>['flush'] = () => {
		state = undefined;
	};

	return {
		getOnce,
		set,
		flush,
	};
};

const placementStageToStageProcessorExecute =
	(placementStage: AnyBackendPlacementStage = identityPlacementStage) =>
	({
		request,
		bypassBuffer,
		enableBypassBuffer,
		tunnelDependencies,
		stageName,
	}: {
		request: RequestContext;
		bypassBuffer: BypassBuffer;
		enableBypassBuffer: boolean;
		tunnelDependencies: TunnelDependencies;
		stageName: string;
	}): AnyPipelineMessageStageProcessorAsyncExecute =>
	async (pipelineMessages) => {
		const placementId = request.orchestrationContext.placementId;
		try {
			const tunnelDecision = await checkShouldTunnelStage({
				request,
				pipelineMessages,
				tunnelDependencies,
				stageName,
				placementId,
			});

			if (tunnelDecision.enabled) {
				return await executePlacementStageTunneled({
					request,
					pipelineMessages,
					stageName,
					placementId,
					messageTemplateIds: tunnelDecision.messageTemplateIds,
					remoteTunnelManager: tunnelDecision.remoteTunnelManager,
					stripUnserializablePayload,
				});
			}
		} catch (error) {
			request.logger.error(
				{ error, stageName, placementId },
				'Error with checking or executing tunnel placement stage. Falling back to production execution.',
			);
			// Fall through to production execution
		}

		const placementMessages = pipelineMessages.map(stripUnserializablePayload);

		const pipelineMessagesByMessageInstanceId: Record<string, PipelineMessage> = Object.fromEntries(
			pipelineMessages.map((pipelineMessage) => [
				pipelineMessage.messageInstanceId,
				pipelineMessage,
			]),
		);

		validateSerializablePayload(placementMessages);

		if (enableBypassBuffer) {
			const bypassInput = bypassBuffer.getOnce();

			const processedData = await placementStage({
				data: { ...bypassInput, messages: placementMessages },
				request,
			});

			const { messages: _, ...bypassOutput } = processedData;

			bypassBuffer.set(bypassOutput);

			const combinedMessages = processedData.messages.map(
				(processedMessage) =>
					({
						...pipelineMessagesByMessageInstanceId[processedMessage.messageInstanceId],
						...processedMessage,
					}) as PipelineMessage,
			);

			return combinedMessages;
		}

		const processedData = await placementStage({
			data: { messages: placementMessages },
			request,
		});

		const combinedMessages = processedData.messages.map(
			(processedMessage) =>
				({
					...pipelineMessagesByMessageInstanceId[processedMessage.messageInstanceId],
					...processedMessage,
				}) as PipelineMessage,
		);

		return combinedMessages;
	};

const stripUnserializablePayload = (pipelineMessage: PipelineMessage): AnyBackendMessageBody => ({
	messageInstanceId: pipelineMessage.messageInstanceId,
	messageTemplateId: pipelineMessage.messageTemplateId,
	messageCategory: pipelineMessage.messageConfig.messageCategory,
	createdAt: pipelineMessage.createdAt,
	eventTime: pipelineMessage.eventTime,
	context: pipelineMessage.context,
	triggerId: pipelineMessage.triggerId,
	messageCreationType: pipelineMessage.messageCreationType,
	recommendationSession: pipelineMessage.recommendationSession,
	readStatus: pipelineMessage.readStatus,
});

const validateSerializablePayload = (payload: unknown): void => {
	try {
		JSON.stringify(payload);
	} catch {
		throw new PlacementSystemError({
			message:
				'failed to backend message body, this may mean a non-serializable field has to been added and should be removed',
		});
	}
};

const identityPlacementStage: AnyBackendPlacementStage = async (input) => input.data;
